import React from 'react';
import { Button } from 'flowbite-react';
import { useGetVotesByQuestion, useGetUserVote } from 'src/hooks/support.hooks';
import { VoteType } from 'types';

interface QuestionVoteButtonsProps {
    questionId: string;
    question: string;
    onVote: (questionId: string, question: string, voteType: VoteType) => void;
    isLoading: boolean;
    /** Rendered after vote buttons; receives current vote counts. Use to e.g. show "Raise a Support Ticket" only when downvotes > 0. */
    renderExtra?: (votes: { upvotes: number; downvotes: number }) => React.ReactNode;
}

const QuestionVoteButtons: React.FC<QuestionVoteButtonsProps> = ({
    questionId,
    question,
    onVote,
    isLoading,
    renderExtra,
}) => {
    const { data: votesData } = useGetVotesByQuestion(questionId);
    const { data: userVoteData } = useGetUserVote(questionId);
    const userVote = userVoteData?.data;
    const upvotes = votesData?.data?.upvotes ?? 0;
    const downvotes = votesData?.data?.downvotes ?? 0;

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#EEEEF5] pt-4 dark:border-gray-700">
                <span className="text-[13px] font-[600] text-[#555770] dark:text-gray-300">Was this information helpful?</span>
                <div className="flex gap-2">
                    {[
                        { type: VoteType.UPVOTE, emoji: '👍', label: 'Helpful' },
                        { type: VoteType.DOWNVOTE, emoji: '👎', label: 'Not helpful' },
                    ].map(option => {
                        const active = userVote?.voteType === option.type;
                        return (
                            <button
                                key={option.type}
                                type="button"
                                aria-label={option.label}
                                aria-pressed={active}
                                disabled={isLoading}
                                onClick={() => onVote(questionId, question, option.type)}
                                className={`inline-flex h-9 items-center gap-1.5 rounded-full border px-3.5 text-[13px] font-[600] transition-all duration-150 active:scale-95 disabled:opacity-60 ${
                                    active
                                        ? 'border-[#8F89F5] bg-[#EEEDFD] text-[#3730A3]'
                                        : 'border-[#E3E3EC] bg-white text-[#555770] hover:border-[#C9C5FA] hover:bg-[#F8F7FF] dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200'
                                }`}
                            >
                                <span className="text-[15px] leading-none">{option.emoji}</span>
                                {option.label}
                            </button>
                        );
                    })}
                </div>
            </div>
            {renderExtra?.({ upvotes, downvotes })}
        </div>
    );
};

export default QuestionVoteButtons;
